import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemePreference = 'light' | 'dark' | 'system';

/**
 * How the sidebar notification bell surfaces unread items:
 * `yes-count` → numeric badge, `yes-badge` → dot indicator, `no` → nothing.
 * UI-only (backend InAppTarget has no field for it), set via the Notifications page.
 */
export type ShowUnreadCountPreference = 'yes-badge' | 'yes-count' | 'no';

interface PreferencesState {
  theme: ThemePreference;
  sidebarCollapsed: boolean;
  /** Pins the sidebar open: hovering the collapsed rail no longer peeks it, and
   *  it never auto-collapses on mouse-leave. Independent of `sidebarCollapsed`
   *  so unpinning returns the rail to whatever collapse state it had. */
  sidebarPinned: boolean;
  chatPanelWidth: number;
  /** Whether the onboarding Lily Agent rail is collapsed to its narrow form. */
  agentPanelCollapsed: boolean;
  /** Whether the experiment-detail Lily Agent panel is hidden. Separate from
   *  `agentPanelCollapsed`, which the enrichment funnel flips per step. */
  experimentAgentPanelCollapsed: boolean;
  /** Sidebar bell unread-count display mode — read by NotificationBell. */
  showUnreadCount: ShowUnreadCountPreference;
  /** Per-table column visibility, keyed by a stable table id then column id.
   *  Sparse on purpose: only columns the user has actually toggled are stored,
   *  so a column added to a table later still arrives at its own default, and
   *  one removed later leaves nothing behind. Read via
   *  `useTableColumnVisibility`, which merges this over the table's defaults. */
  tableColumns: Record<string, Record<string, boolean>>;
  /** Per-table column order, keyed by a stable table id → ordered column ids.
   *  Reconciled against the table's current columns on read (see
   *  `useTableColumnOrder`), so ids that no longer exist are dropped and new
   *  ones are appended rather than lost. */
  tableColumnOrder: Record<string, string[]>;
}

interface PreferencesActions {
  setTheme: (theme: ThemePreference) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarPinned: (pinned: boolean) => void;
  setChatPanelWidth: (width: number) => void;
  setAgentPanelCollapsed: (collapsed: boolean) => void;
  setExperimentAgentPanelCollapsed: (collapsed: boolean) => void;
  setShowUnreadCount: (mode: ShowUnreadCountPreference) => void;
  /** Remember one column's visibility for one table. */
  setTableColumn: (tableId: string, columnId: string, visible: boolean) => void;
  /** Remember the full column order for one table. */
  setTableColumnOrder: (tableId: string, order: string[]) => void;
}

/** Preferences store — persisted to localStorage. */
export const usePreferencesStore = create<PreferencesState & PreferencesActions>()(
  persist(
    (set) => ({
      theme: 'system',
      sidebarCollapsed: false,
      sidebarPinned: false,
      chatPanelWidth: 0, // 0 = use default 40% ratio
      agentPanelCollapsed: false,
      experimentAgentPanelCollapsed: false,
      showUnreadCount: 'yes-badge',
      tableColumns: {},
      tableColumnOrder: {},

      setTheme: (theme) => set({ theme }),

      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      setSidebarPinned: (pinned) => set({ sidebarPinned: pinned }),

      setChatPanelWidth: (width) => set({ chatPanelWidth: width }),

      setAgentPanelCollapsed: (collapsed) => set({ agentPanelCollapsed: collapsed }),

      setExperimentAgentPanelCollapsed: (collapsed) =>
        set({ experimentAgentPanelCollapsed: collapsed }),

      setShowUnreadCount: (mode) => set({ showUnreadCount: mode }),

      setTableColumn: (tableId, columnId, visible) =>
        set((state) => ({
          tableColumns: {
            ...state.tableColumns,
            [tableId]: { ...state.tableColumns[tableId], [columnId]: visible },
          },
        })),

      setTableColumnOrder: (tableId, order) =>
        set((state) => ({
          tableColumnOrder: { ...state.tableColumnOrder, [tableId]: order },
        })),
    }),
    {
      name: 'app-preferences-store',
      // v3 — added `sidebarPinned` (pin the sidebar open against hover-peek).
      //
      // Deliberately NOT bumped for `tableColumns`. There is no `migrate`
      // function here, and zustand drops the whole persisted blob when the
      // version moves without one — so a bump would silently reset everyone's
      // theme and sidebar preferences. Adding a field needs no bump anyway:
      // the default shallow merge lays the stored state over the initial one,
      // so an older payload simply arrives without `tableColumns` and it keeps
      // its `{}` default. Only a *breaking reshape* of existing fields needs a
      // version bump, and that one must ship with a `migrate`.
      version: 3,
    },
  ),
);
